/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {InputToggleDropdownModel} from './models/input-toggle-dropdown.model';
import {
    Observable,
    of
} from 'rxjs';
import {AbstractControl} from '@angular/forms';
import {ReferenceDataModel} from '../../../../../api/reference-data.model';
import {TypeaheadHelperService} from '../../../../../shared/services/typeahead-helper.service';
import {StatefulComponent} from '../../../../../shared/stateful/stateful.component';
import {InputDropdownComponent} from '../../../../../shared/input/input-dropdown/input-dropdown.component';
import {InputMessagesService} from '../../../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../../../shared/warnings/warnings.enum';
import {MandatoryMessageTypeEnum} from '../../../../../shared/input/service/mandatory-message-type.enum';
import {InputToggleDropdownState} from './models/input-toggle-dropdown.state';
import {InputToggleDropdownConfiguration} from './configuration/input-toggle-dropdown.configuration';
import {SwitchButtonModel} from '../../../../../shared/switch-button/models/switch-button.model';
import {BundlingSettingsInputsEmitterModel} from '../models/bundling-settings-inputs-emitter.model';

@Component({
    selector: 'app-input-toggle-dropdown',
    templateUrl: './input-toggle-dropdown.component.html',
    styleUrls: ['./input-toggle-dropdown.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputToggleDropdownComponent extends StatefulComponent<InputToggleDropdownState> implements OnInit, AfterViewInit, OnChanges {
    @Input() inputToggleDropdownConfiguration: InputToggleDropdownConfiguration;
    @Input() inputToggleDropdownModel: InputToggleDropdownModel;
    @Input() dropdownResults: ReferenceDataModel[] = [];
    @Output() valueChange = new EventEmitter<BundlingSettingsInputsEmitterModel>();
    @Output() inputToggleChange = new EventEmitter<boolean>();
    @ViewChild('inputDropdown') inputDropdown: InputDropdownComponent;
    public inputToggleDropdownMessages = new Map<WarningsEnum, string[]>();
    public WarningsEnum = WarningsEnum;
    public defaultInputDropdownPlaceholder = 'Select';
    public switchButtonModel: SwitchButtonModel;

    constructor(public typeahead: TypeaheadHelperService,
                private inputMessagesService: InputMessagesService,
                private cdr: ChangeDetectorRef) {
        super();
    }

    ngOnInit(): void {
        this.populateSwitchButton();
        this.storeState({
            initialReferenceData: this.transformValueInReferenceDataModel(this.inputToggleDropdownModel.value)
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['inputToggleDropdownModel']?.firstChange &&
                changes['inputToggleDropdownModel']?.previousValue !== changes['inputToggleDropdownModel']?.currentValue) {
            this.populateSwitchButton();
            this.storeState({
                initialReferenceData: this.transformValueInReferenceDataModel(this.inputToggleDropdownModel.value)
            });
            this.toggleInputDropdownHandler(this.inputToggleDropdownModel.toggleActive);
        }
    }

    ngAfterViewInit(): void {
        this.toggleInputDropdownHandler(this.inputToggleDropdownModel.toggleActive);
        this.cdr.detectChanges();
    }

    get inputDropdownPlaceholder(): string {
        return `${this.defaultInputDropdownPlaceholder} ${this.inputToggleDropdownConfiguration.placeholder.toLowerCase()}`;
    }

    inputToggleDropdownDataCy(dropdownCode: string): string {
        let dataCy = 'input-toggle-dropdown';
        if (this.inputToggleDropdownModel.viewMode) {
            dataCy = `${dataCy}-${dropdownCode.toLowerCase()}-view-mode`;
        } else {
            dataCy = `${dataCy}-${dropdownCode.toLowerCase()}-edit-mode`;
        }
        return dataCy;
    }

    searchFunction = (term: string): Observable<ReferenceDataModel[]> => {
        if (term != null && term.length > 0) {
            return of(this.dropdownResults?.filter((item) => item.name
                    .toLowerCase()
                    .indexOf(term.toLowerCase()) > -1));
        }

        return of(this.dropdownResults);
    };

    toggleInputDropdownHandler(active: boolean): void {
        this.storeState({isToggleActive: active});
        if (active) {
            this.inputDropdown.enable();
            this.populateInputDropdown();
            this.validateInputDropdownValue();
            this.checkIfDropdownShouldBeDisabled();
        } else {
            this.inputDropdown.reset();
            this.inputDropdown.disable();
        }
    }

    /**
     * Manages the input status changes, adding errors and warnings as needed
     */
    manageInputStateChange(input: AbstractControl): void {
        this.inputMessagesService.mandatoryWarnings(input,
                this.inputToggleDropdownMessages,
                MandatoryMessageTypeEnum.APPLY);
        this.inputMessagesService.invalidInputValueError(input,
                this.inputToggleDropdownMessages,
                this.inputToggleDropdownConfiguration.placeholder.toLowerCase());
        this.inputToggleDropdownMessages = new Map(this.inputToggleDropdownMessages);
    }

    manageInputValueChange(option: ReferenceDataModel): void {
        this.valueChange.emit({
            code: this.inputToggleDropdownConfiguration.code,
            value: option?.code,
            isValid: this.isInputDropdownValid || !this.state.isToggleActive,
            isDisabled: this.inputDropdown.isDisabled
        });
    }

    private get isInputDropdownValid(): boolean {
        return this.inputDropdown.isValid && this.inputDropdown.inputControl.value != null;
    }

    private inputReferenceDataModelOnDropdownResults(input: string): ReferenceDataModel {
        return this.dropdownResults?.find((elem) => elem.name === input);
    }

    private transformValueInReferenceDataModel(input: string): ReferenceDataModel {
        let inputReferenceData = this.inputReferenceDataModelOnDropdownResults(input);
        if (!inputReferenceData) {
            // Define a object just for matching the typeahead.inputTextNameFormatter to allow the input to be shown on the dropdown
            inputReferenceData = {
                code: null,
                name: input ?? '-',
                active: true
            };
        }
        return inputReferenceData;
    }

    private populateSwitchButton(): void {
        this.switchButtonModel = {
            checked: this.inputToggleDropdownModel.toggleActive,
            disabled: this.inputToggleDropdownModel.viewMode || this.inputToggleDropdownModel.toggleDisable
        };
    }

    private populateInputDropdown(): void {
        if (this.inputToggleDropdownModel.value == null) {
            if (this.dropdownResults?.length === 1) {
                this.inputDropdown?.setValue(this.dropdownResults[0]);
            } else {
                this.inputDropdown.reset();
            }
        } else {
            this.inputDropdown?.setValue(this.state.initialReferenceData);
        }
    }

    private validateInputDropdownValue(): void {
        if (this.inputDropdown.inputControl.value != null &&
                this.dropdownResults?.find((elem) => elem.code === this.inputDropdown.inputControl.value?.code) == null) {
            this.inputDropdown?.markAsInvalid();
        }
    }

    private checkIfDropdownShouldBeDisabled(): void {
        if (this.inputDropdown.isValid && this.dropdownResults.length === 1) {
            this.inputDropdown.disable();
        }
    }
}
